import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { filter, find, findIndex } from 'ramda';
import Multilingual from '~~utils/Multilingual';
import UploadImages from '~~elements/UploadImages';
import UploadVideos from '~~elements/UploadVideos';
import { TABLET_WIDTH } from '~~elements/Const';
import { GameSectionDiv } from '../styled';

const StyledDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 1rem;
  column-gap: 1rem;

  > div {
    position: relative;
    width: 100%;
    box-shadow: 2px 2px 6px 0 #000;

    &:after {
      content: '';
      display: block;
      padding-bottom: 100%;
    }
    .close {
      position: absolute;
      font-size: 1.5rem;
      text-shadow: 1px 1px 1px white;
      top: 0;
      right: 1rem;
      cursor: pointer;
    }
    > img {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    > video {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .upload-area {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #eee;
  }

  @media (max-width: ${TABLET_WIDTH}) {
    grid-template-columns: 1fr;
  }
`;

const freeMediaQuota = 6;
const premiumMediaQuota = 50;

const GameMediaSection = ({ isPremium, mediaList, onChange, apiInProgress }) => {
  const [removedMediaList, setRemovedMediaList] = useState([]);
  const [addedMediaList, setAddedMediaList] = useState([]);

  const [mediaQuota, setMediaQuota] = useState(freeMediaQuota);

  useEffect(() => {
    if (removedMediaList.length === 0 && addedMediaList.length === 0) {
      onChange(null);
      return;
    }

    onChange({ removedMediaList, addedMediaList });
  }, [addedMediaList, onChange, removedMediaList]);

  useEffect(() => {
    setMediaQuota(isPremium ? premiumMediaQuota : freeMediaQuota);
  }, [isPremium]);

  const handleMediaDelete = (path) => () => {
    if (apiInProgress) {
      return;
    }

    const media = find((m) => m.path === path, mediaList);
    if (!media) {
      return;
    }

    setRemovedMediaList([...removedMediaList, { path, type: media.type }]);
  };

  const handleMediaUpload = (mediaType) => (newMedias) => {
    console.log(newMedias);
    if (!Array.isArray(newMedias) || newMedias.length === 0) {
      return;
    }

    const maxLength =
      mediaQuota -
      (filter((m) => !find((rm) => rm.path === m.path, removedMediaList), mediaList).length + addedMediaList.length);
    if (maxLength < 1) {
      return;
    }
    setAddedMediaList([
      ...addedMediaList,
      ...newMedias.map((img) => ({ type: mediaType, path: img.path, url: img.url })).slice(0, maxLength),
    ]);
  };

  const handleMediaUploadCancel = (path) => () => {
    if (apiInProgress) {
      return;
    }

    const mediaIndex = findIndex((m) => m.path === path, addedMediaList);
    if (mediaIndex < 0) {
      return;
    }

    addedMediaList.splice(mediaIndex, 1);
    setAddedMediaList([...addedMediaList]);
  };

  return (
    <GameSectionDiv>
      <h3 className="section--title">{Multilingual(`GAME.MEDIA`)}</h3>
      <div className="section--description">
        {isPremium
          ? Multilingual(`TOURNAMENT.MEDIA_UPLOAD_PREMIUM_DESCRIPTION`)
          : Multilingual(`TOURNAMENT.MEDIA_UPLOAD_DESCRIPTION`)}
      </div>
      <StyledDiv>
        {filter((m) => !find((rm) => rm.path === m.path, removedMediaList), mediaList).map((media) => (
          <div key={media.path}>
            {media.type === 'VIDEO' ? (
              <video controls playsInline>
                <source src={`${media.url}#t=0.001`} type="video/mp4" />
              </video>
            ) : (
              <img src={media.url} alt={media.path} />
            )}
            <i className="close" onClick={handleMediaDelete(media.path)}>
              &times;
            </i>
          </div>
        ))}
        {addedMediaList.map((media) => (
          <div key={media.path}>
            {media.type === 'VIDEO' ? (
              <video controls>
                <source src={media.url} type="video/mp4" />
              </video>
            ) : (
              <img src={media.url} alt={media.path} />
            )}
            <i className="close" onClick={handleMediaUploadCancel(media.path)}>
              &times;
            </i>
          </div>
        ))}
        {filter((m) => !find((rm) => rm.path === m.path, removedMediaList), mediaList).length + addedMediaList.length <
          mediaQuota && (
          <Fragment>
            <UploadImages size="5rem" url="/api/media" multi={true} onChange={handleMediaUpload('PHOTO')} />
            <UploadVideos size="5rem" url="/api/media" multi={true} onChange={handleMediaUpload('VIDEO')} />
          </Fragment>
        )}
      </StyledDiv>
    </GameSectionDiv>
  );
};

export default GameMediaSection;
